/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import XML from 'apmui/util/XML';
import deepCopy from 'apmui/util/deepCopy';
import { RESOURCE, RESOURCE_VALUES, RESOURCE_TO_SECTION } from './ResourceType';

enum ERROR {
    RESOURCE_LIST_INVALID = 4100,
    RESOURCE_LIST_NO_RESOURCES = 4101,
    RESOURCE_INFO_NO_RESOURCES = 4102,
}

enum WARNING {
    RESOURCE_INFO_MISSING_RESOURCES = 4120,
}

enum RESOURCE_LIST_URL {
    WEBTOP = '/vdesk/resource_list.xml?resourcetype=res',
    OAUTH = '/vdesk/resource_list.xml?resourcetype=oauth',
}

/** parititon name to weight */
type IPrefixWeight = Record<string, number>;

interface IRequestedResourceId {
    type: RESOURCE;
    /** resource id or id:customizationGroupId */
    fullId: string;
    /** fullId, may be without partition, :customizationGroupId is also losing partition if present */
    rqId: string;
}

interface IQuery {
    /** resource info request url */
    url: string;
    /** request url length calculated ahead of url creation */
    length: number;
    /** optimized partition, '' if no */
    partition: string;
    /*  &prtn= is already considered in length */
    hasPrtn: boolean;
    resourceIdList: IRequestedResourceId[];
    /* &type= is considered in length */
    hasType: { [k in RESOURCE]?: number };
}

interface IResourceId {
    type: RESOURCE;
    id: string;
}

interface IResourceProps {
    /** customization group id aka cgId */
    customizationGroupId?: string;
    /** all properties from resourceInfo */
    props: Record<string, string>;
    /** resources assigned into webtop section */
    assignedResources?: IResourceId[];
}

interface IResourceGroup {
    /** get param name for this group, see qurey froming/resource group loading */
    param: string;
    /** key is id (not fullId), value is resource */
    resources: Record<string, IResourceProps>;
}

type IResourceMap = { [type in RESOURCE]?: IResourceGroup };

interface IResourceMapLoaded {
    resourceMap: IResourceMap;
    warning?: WARNING;
}

interface IResourceLoadingFailed {
    error: ERROR;
}

/**
 * How this class works:
 * 1. It sends request to resourceListUrl
 * 2. Parses response and creates multiple queries
 * 3. Converts queries to urls and sends multiple requests to this.resourceInfoUrl
 * 4. Processes resources
 */

class ResourceLoader {
    /** Default url for resource info */
    static readonly REQUEST_INFO_URL = '/vdesk/resource_info_v2.xml';
    /** Default max length of single resource info request */
    static readonly REQUEST_INFO_MAX_LENGTH = 2048;
    /** Partition url param */
    static readonly PRTN: string = '&prtn=';
    /** Default for partition optimization */
    static readonly PARTITION_OPTIMIZATION = false;

    /**
     * Called when resource list is received and processed with no errors
     * @param resourceMap    all resources without filled props
     */
    public onResourceList: (resourceMap: IResourceMap) => void;
    /**
     * Called when one of resource info requests is received and processed
     * @param resourceMapChunk    received resources with filled props
     */
    public onResourceInfo: (resourceMapChunk: IResourceMap) => void;
    /**
     * Called when all or resource info requests are received and processed
     * @param resourceMap    all resources with filled props
     * @param warning      number if something went wrong during resource info loading, otherwise undefined
     */
    public onComplete: (resourceMap: IResourceMap, warning: WARNING | undefined) => void;
    /**
     * Called if fatal error has occurred during loading resource list or resource info
     * @param resourceMapChunk      All resources with filled props
     * @param error     Error number
     */
    public onFailure: (error: ERROR) => void; // something went wrong

    /** Url for request info */
    private requestInfoUrl: string;
    /** Maximum of length of GET request to requestInfoUrl */
    private requestInfoMaxLength: number;
    /** HTTP Method of resource info request */
    private resourceInfoMethod: string;
    /** Whether we cut partition for id and user PRTN as part of request or not */
    private optimizePartition: boolean;

    /** All resources */
    private resourceMap: IResourceMap;
    /** Number of resourceIdList entries with info hasn't been received yet */
    private resourcesRemained: number;
    /** Queries that has been requested and not processed yet */
    private activeQueries: number;
    /** to be returned with onComplete function */
    private warning: WARNING | undefined;

    constructor() {
        this.requestInfoMaxLength = ResourceLoader.REQUEST_INFO_MAX_LENGTH;
        this.requestInfoUrl = ResourceLoader.REQUEST_INFO_URL;
        this.optimizePartition = ResourceLoader.PARTITION_OPTIMIZATION;

        this.resourceMap = {};
        this.resourceInfoMethod = 'GET';

        this.activeQueries = 0;
        this.warning = undefined;
    }
    /**
     * Creates request to resourceList url
     * @param resourceListUrl   url for resource list request
     */
    public load(resourceListUrl: RESOURCE_LIST_URL = RESOURCE_LIST_URL.WEBTOP) {
        const resourceListXHR = new XMLHttpRequest();
        resourceListXHR.onreadystatechange = this.resourceListDispatch.bind(this, resourceListXHR);
        resourceListXHR.open('GET', resourceListUrl, true);
        resourceListXHR.send(null);
    }
    /**
     * Callback of resourceListXHR, check if response is correct and dispatches to resourceListProcess
     * @param resourceListXHR   xhr for resourceListUrl
     */
    private resourceListDispatch(resourceListXHR: XMLHttpRequest) {
        if (resourceListXHR.readyState !== resourceListXHR.DONE) {
            return; // resourceIdListResponse(resourceIdListXHR: XMLHttpRequest)
        }
        switch (resourceListXHR.status) {
            case 200: {
                const errorCode = this.resourceListProcess(resourceListXHR);
                if (errorCode !== undefined) {
                    this.onFailure(errorCode);
                }
                break;
            }
            default:
                this.onFailure(ERROR.RESOURCE_LIST_INVALID);
        }
    }
    /**
     * Processes resourceListXHR and creates this.resourceMap and resourceIdList, then calls this onResourceInfo
     * @param resourceListXHR    Request to requestListUrl
     */
    private resourceListProcess(resourceListXHR: XMLHttpRequest): ERROR | undefined {
        //
        if (!resourceListXHR.responseXML) {
            return ERROR.RESOURCE_LIST_INVALID;
        }
        // console.log(resourceListXHR.responseXML);

        // update default options from request options
        const options = resourceListXHR.responseXML.querySelector('opts opt[type=available_rq]');
        if (options) {
            this.resourceInfoMethod = XML.getAttribute(options, 'method', this.resourceInfoMethod);
            this.requestInfoMaxLength = XML.getAttributeInt(options, 'max_uri_size', this.requestInfoMaxLength);
            this.requestInfoUrl = XML.getAttribute(options, 'uri', this.requestInfoUrl);
            this.optimizePartition = !!XML.getAttributeInt(options, 'prtn_opt', this.optimizePartition ? 1 : 0);
        }

        /*
         * <list type='RESOURCE'>
         *    <entry type='group_names' param='abc_list'>/Common/std /Common/non-std</entry>
         *    [<entry type='customization_group_names' param='abc_grps'>/Common/non-std:/Common/non-std-cgrp</entry>]
         * </list>
         */
        const resourceIdList: IRequestedResourceId[] = []; // array of resources for query creation
        const lists = resourceListXHR.responseXML.querySelectorAll('lists list[type]');

        iter: for (let i = 0; i < lists.length; i++) {
            const list = lists[i];
            const type = list.getAttribute('type') as RESOURCE;
            if (RESOURCE_VALUES.indexOf(type) === -1) {
                // TODO Warning code?
                console.log(`Invalid list type ${type}`);
                return ERROR.RESOURCE_LIST_INVALID;
            }

            const entrycgn = list.querySelector('entry[type=customization_group_names]');
            // fullIds is map where key is resourceId: value is resourceId:customizationGroupId
            const cgmap: { [id: string]: string } = {};
            if (entrycgn && entrycgn.firstChild && entrycgn.firstChild.nodeValue) {
                entrycgn.firstChild.nodeValue.split(' ').forEach((fullId: string) => {
                    const [id, customizationGroupId] = fullId.split(':');
                    cgmap[id] = customizationGroupId;
                });
            }

            const entrygn = list.querySelector('entry[type=group_names]');
            if (!entrygn || !entrygn.firstChild || !entrygn.firstChild.nodeValue) {
                continue iter; // for (let i = 0; i < lists.length; i++)
            }
            // group of RESOURCE type
            const group: IResourceGroup = {
                param: '&' + entrygn.getAttribute('param') + '=',
                resources: {},
            };
            entrygn.firstChild.nodeValue.split(' ').forEach((id: string) => {
                if (!group.resources[id]) {
                    group.resources[id] = { props: {} };
                    let fullId = id;
                    if (cgmap[id]) {
                        fullId += ':' + cgmap[id];
                        group.resources[id].customizationGroupId = cgmap[id];
                    }
                    if (type === RESOURCE.WEBTOP_SECTION) {
                        group.resources[id].assignedResources = [];
                    }
                    resourceIdList.push({ fullId, rqId: fullId, type }); // TODO: fullId or
                }
            }); // entry.firstChild.nodeValue.split(' ').forEach((id: string)

            /*
             * Webtop section could also has special entry inside <list>
             * <entry type='webtop_section_resource'>/Common/webtop-section:4:0:/Common/newwt-altavista</entry>
             * Entries are sectionId:priority:resourceTypeNum:resourceId
             * priority: priority of resource inside webtop section, resourceTypeNum see TYPENUM_TO_RESOURCE
             */
            if (type === RESOURCE.WEBTOP_SECTION) {
                const entrywsr = list.querySelector('entry[type=webtop_section_resource]');
                if (
                    entrywsr !== null &&
                    entrywsr.firstChild !== null &&
                    entrywsr.firstChild.nodeValue !== null &&
                    entrywsr.firstChild.nodeValue !== ''
                ) {
                    // split strings, push into appropriate webtop_sections
                    entrywsr.firstChild.nodeValue.split(' ').forEach((p: string) => {
                        const [sectionId, priority, resourceTypeNum, resourceId] = p.split(':'); // all strings
                        const section = group.resources[sectionId];
                        if (section && section.assignedResources) {
                            const resourceType = RESOURCE_TO_SECTION[parseInt(resourceTypeNum, 10)];
                            section.assignedResources[parseInt(priority, 10)] = {
                                type: resourceType,
                                id: resourceId,
                            };
                        }
                    });
                    // filter out holes in section.assignedRes (k: priority)
                    Object.keys(group.resources).forEach((id: string) => {
                        const section = group.resources[id];
                        if (section.assignedResources) {
                            section.assignedResources = section.assignedResources.filter((v: any) => v !== undefined);
                        }
                    });
                }
            } // if (type === 'webtop_section')
            this.resourceMap[type] = group; // add group to resource list
        } // iter: for (let i = 0; i < lists.length; i++)
        // console.log(this.resourceMap);

        this.resourcesRemained = resourceIdList.length;
        if (this.resourcesRemained === 0) {
            return ERROR.RESOURCE_LIST_NO_RESOURCES;
        }

        // call callback
        if (typeof this.onResourceList === 'function') {
            const resourceMap = deepCopy(this.resourceMap);
            this.onResourceList(resourceMap);
        }

        // create request urls and get them
        const queries = this.resourceInfoLoad(resourceIdList);
        for (const query of queries) {
            this.resourceInfoRequest(query);
        }

        // no worries here
        return undefined;
    }
    /**
     * Updates weight of partition(/folder) in weight
     * E.g. for id='/a/b/c/id' => weight['a/b/c'] += 'a/b/c'.length, weight['a/b'] += 'a/b', weight['a'] += 'a'.length
     * @param id: string                 resource id in format /partition(/folder){0,}/name
     * @param weights: IPrefixWeight     key: id, value: id.length * repeats
     */
    private countPartitions(/** @const */ id: string, weights: IPrefixWeight) {
        // name is /part/fl/name format -> ['part', 'fl', 'name']
        const splits = id.substr(1).split('/');
        // removes name
        splits.pop();
        for (let j = splits.length - 1; j >= 0; j--) {
            if (j > splits.length - 1) {
                splits[j] += '/' + splits[j + 1]; // segment = 'part', 'fl' -> part/fl
            }
            // weights is calculated like segment length * times
            if (weights[splits[j]] === undefined) {
                weights[splits[j]] = splits[j].length;
            } else {
                weights[splits[j]] += splits[j].length;
            }
        }
    }
    /**
     * Creates map of all partition(/folder) with this.countPartitions, finds and returns most optimal
     * @param resourceIdList   list of resources
     */
    private findOptimalPartition(/** @const */ resourceIdList: IRequestedResourceId[]) {
        const weights: IPrefixWeight = {};
        for (const resourceId of resourceIdList) {
            resourceId.fullId.split(':').forEach((n: string) => this.countPartitions(n, weights));
        }
        let partition = '';
        let weight = 0;
        Object.keys(weights).forEach((k: string) => {
            if (weights[k] > weight) {
                partition = k;
                weight = weights[k];
            }
        });
        return '/' + partition + '/';
    }
    /**
     * Returns default empty query object
     */
    private getDefaultQuery(/** @const */ partition: string = ''): IQuery {
        return {
            url: '',
            length: this.requestInfoUrl.length,
            hasPrtn: false,
            partition,
            resourceIdList: [],
            hasType: {},
        };
    }
    /**
     * Cutting this.optimalPartition in resourceIdList, sorts it then creates queries using this.addToQuery
     * Calls this.resourceInfoRequest on all created queries
     * @param resourceIdList    list of resources
     */
    private resourceInfoLoad(resourceIdList: IRequestedResourceId[]): IQuery[] {
        // preliminary sort
        resourceIdList.sort((a: IRequestedResourceId, b: IRequestedResourceId) => a.fullId.length - b.fullId.length);
        //
        const queries: IQuery[] = [];
        while (resourceIdList.length) {
            // find optimal partition or partition(/folder)
            let partition = '';
            if (this.optimizePartition) {
                partition = this.findOptimalPartition(resourceIdList);
                // console.log('optimalPartition = ' + partition);
                if (partition === '') {
                    for (const resourceId of resourceIdList) {
                        resourceId.rqId = resourceId.fullId;
                    }
                } else {
                    const reg = new RegExp('(^|:)' + partition, 'g'); // id|:cgid
                    for (const resourceId of resourceIdList) {
                        resourceId.rqId = resourceId.fullId.replace(reg, '$1');
                    }
                    // sort resources
                }
                resourceIdList.sort(
                    (a: IRequestedResourceId, b: IRequestedResourceId) => a.rqId.length - b.rqId.length
                );
            }

            const query = this.getDefaultQuery(partition);
            // adds resources from beginning of sorted list to query
            let first = 0;
            while (resourceIdList[first] && this.addResourceToQuery(resourceIdList[first], query)) {
                first++;
            }
            resourceIdList.splice(0, first);
            // adds resource from the end
            let last = resourceIdList.length - 1;
            while (resourceIdList[last] && this.addResourceToQuery(resourceIdList[last], query)) {
                last--;
            }
            resourceIdList.splice(last + 1);
            // done
            queries.push(query);
        }
        return queries;
    }
    /*
     * Calculates whether query url length with given would exceed max
     * @param resourceIdList    list of resources to get resource from*
     * @param query   current query
     */
    private addResourceToQuery(resourceId: IRequestedResourceId, query: IQuery): boolean {
        // no this update
        if (!resourceId) {
            return false;
        }
        const { rqId, type } = resourceId;
        // see do we need prefix still
        let prefixLength = 0;
        if (query.partition && !query.hasPrtn && rqId.substr(0, 1) !== '/') {
            // &a=a -> &a=a&prtn=b
            prefixLength = ResourceLoader.PRTN.length + query.partition.length;
        }
        // if this is first request &cur=a -> &cur=a&new=b else &cur=a -> &cur=a+b
        const paramLength = query.hasType[type] ? 1 : this.resourceMap[type]!.param.length;
        // aaabbb + &something=
        if (query.length + paramLength + rqId.length + prefixLength > this.requestInfoMaxLength) {
            // sum of lengths is longer than maximum request length
            return false;
        }
        // increment length
        query.length += paramLength + rqId.length + prefixLength;
        // set that query is using this particular request type
        query.hasType[type] = 1;
        query.resourceIdList.push(resourceId);
        // if prefixLength is non zero add it to request later - prevent adding more times
        if (prefixLength) {
            query.hasPrtn = true;
        }
        return true;
    }
    /**
     * Creates query.url and sends request to it, increases this.activeQueries
     * @param query   current query
     */
    private resourceInfoRequest(query: IQuery) {
        query.url = '';
        if (query.hasPrtn) {
            query.url += ResourceLoader.PRTN + query.partition;
        }
        let type = '';
        // sort resource by type to avoid multiple inclusions of query_url
        query.resourceIdList.sort((a: IRequestedResourceId, b: IRequestedResourceId) => a.type.localeCompare(b.type));
        for (const resource of query.resourceIdList) {
            if (resource.type === type) {
                query.url += '+';
            } else {
                type = resource.type;
                query.url += this.resourceMap[type].param;
            }
            query.url += resource.rqId;
        }
        query.url = this.requestInfoUrl + '?' + query.url.substr(1);
        // console.log('query.url.length = ' + query.url.length);
        // console.log(query);

        // query is being requested, increase number of active queries
        this.activeQueries++;

        // creating request
        const resourceInfoXHR = new XMLHttpRequest();
        resourceInfoXHR.onreadystatechange = this.resourceInfoDispatch.bind(this, resourceInfoXHR, query);
        resourceInfoXHR.open(this.resourceInfoMethod, query.url, true);
        resourceInfoXHR.send(null);
    }
    /**
     * Receives and processes resourceInfoXHR, reduces this.activeQueries
     */
    private resourceInfoDispatch(resourceInfoXHR: XMLHttpRequest, query: IQuery) {
        if (resourceInfoXHR.readyState !== resourceInfoXHR.DONE) {
            return;
        }
        switch (resourceInfoXHR.status) {
            case 200:
                this.resourceInfoProcess(resourceInfoXHR);
                break;
            case 414:
                this.resourceInfoRerequest(query);
                break;
            default:
                this.warning = WARNING.RESOURCE_INFO_MISSING_RESOURCES;
        }
        // query has been returned and processed, reducing count of active queries
        this.activeQueries--;
        if (this.activeQueries === 0) {
            console.log(`onComplete, resourcesRemained=${this.resourcesRemained}`);
            if (this.resourcesRemained && !this.warning) {
                this.warning = WARNING.RESOURCE_INFO_MISSING_RESOURCES;
            }
            console.log(this.resourceMap);
            if (typeof this.onComplete === 'function') {
                this.onComplete(this.resourceMap, this.warning);
            }
        }
    }
    /**
     * Properties where single quote is overly escaped ( \' -> ' )
     */
    static readonly propsSingleQuote = ['caption', 'description'];
    /**
     * Transforms XML to IResourceInfo (json like) object
     * @param resourceInfoXHR: XMLHttpRequest    request to this.requestInfoUrl
     */
    private resourceInfoProcess(resourceInfoXHR: XMLHttpRequest) {
        if (!resourceInfoXHR.responseXML) {
            this.warning = WARNING.RESOURCE_INFO_MISSING_RESOURCES;
            return;
        }

        const resourceMapChunk: IResourceMap = {};
        const items = resourceInfoXHR.responseXML.querySelectorAll('item');

        iter: for (let i = 0; i < items.length; i++) {
            const props: Record<string, string> = {};

            for (let j = 0; j < items[i].childNodes.length; j++) {
                const element = items[i].childNodes[j] as Element;
                const prop = element.tagName;
                props[prop] = '';
                if (element.nodeType === Node.ELEMENT_NODE && element.firstChild && element.firstChild.nodeValue) {
                    if (ResourceLoader.propsSingleQuote.indexOf(prop) !== -1) {
                        props[prop] = element.firstChild.nodeValue.replace("\\'", "'");
                    } else {
                        props[prop] = element.firstChild.nodeValue;
                    }
                }
            }
            if (!props.id || !props.type) {
                continue iter; // iter: for (let i = 0; i < items.length; i++)
            }
            if (props.type === 'web_app') {
                props.type = RESOURCE.PORTAL_ACCESS;
            }
            const group: IResourceGroup = this.resourceMap[props.type];
            if (!group || !group.resources[props.id]) {
                console.log(`Weird response: ${props.type} ${props.id} is not found in resourceMap`);
                continue iter; // iter: for (let i = 0; i < items.length; i++)
            }
            group.resources[props.id].props = props;
            if (!resourceMapChunk[props.type]) {
                resourceMapChunk[props.type] = { param: group.param, resources: {} };
            }
            resourceMapChunk[props.type].resources[props.id] = group.resources[props.id];
            this.resourcesRemained--;
        }
        // console.log(`activeQueries=${this.activeQueries}, resourcesRemained=${this.resourcesRemained}`);
        // console.log(resourceMapChunk);

        if (typeof this.onResourceInfo === 'function') {
            this.onResourceInfo(resourceMapChunk);
        }
    }
    /**
     * If resourceInfoDispatch received 414 this function is trying to split request on 2 parts and re-sent it
     * @param query:IQuery   query that received 414
     */
    private resourceInfoRerequest(/** @const */ failedQuery: IQuery) {
        const resourceLists: IRequestedResourceId[][] = [[], []];
        let i = 0;
        for (const res of failedQuery.resourceIdList) {
            resourceLists[i].push(res);
            i = 1 - i;
        }
        for (const resourceList of resourceLists) {
            const newQueries = this.resourceInfoLoad(resourceList);
            for (const newQuery of newQueries) {
                this.resourceInfoRequest(newQuery);
            }
        }
    }
}

export {
    ResourceLoader as default,
    ResourceLoader,
    IResourceId,
    IResourceMap,
    IResourceGroup,
    IResourceProps,
    RESOURCE_LIST_URL,
    WARNING,
    ERROR,
    IResourceMapLoaded,
    IResourceLoadingFailed,
};
